import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfig } from '../config/ConfigContext';
import useMediaQuery from '../hooks/useMediaQuery';

/**
 * Minimalist PublicationCard component with elegant design
 * @param {Object} props - Component props
 * @param {Object} props.publication - Publication data object
 * @param {Function} props.onClick - Optional click handler function
 * @returns {JSX.Element} PublicationCard component
 */
const PublicationCard = ({ publication, onClick }) => {
  const { theme } = useConfig();
  const navigate = useNavigate();
  const isMobileView = useMediaQuery('(max-width: 768px)');

  // Ensure publication object exists with default values
  const pubData = publication || {};

  // Get subtle type color
  const getTypeColor = (type) => {
    const typeColors = {
      'book': '#2563eb', // Blue
      'booklet': '#16a34a', // Green
      'notes': '#ea580c', // Orange
      'guide': '#9333ea', // Purple
      'manual': '#dc2626', // Red
      'default': '#6b7280' // Gray
    };

    const normalizedType = (type || '').toLowerCase().trim();
    return typeColors[normalizedType] || typeColors.default;
  };

  // Handle card click - navigate to PDF viewer
  const handleCardClick = () => {
    if (onClick) {
      onClick(pubData);
    }
    
    if (pubData.id && pubData.pdfUrl) {
      navigate(`/publication/${pubData.id}`);
    } else if (pubData.pdfUrl) {
      // Fallback: open PDF in new tab if no viewer available
      window.open(pubData.pdfUrl, '_blank');
    }
  };

  // Define styles for desktop and mobile
  const cardStyles = {
    container: {
      backgroundColor: 'white',
      borderRadius: '8px',
      cursor: pubData.pdfUrl ? 'pointer' : 'default',
      transition: 'all 0.2s ease',
      border: '1px solid #e5e7eb',
      position: 'relative',
      // Different approach for mobile vs desktop
      ...(isMobileView ? {
        minHeight: '220px',
        padding: '0',
        position: 'relative',
        display: 'block'
      } : {
        height: '240px', // Reduced from 280px
        padding: '0',
        display: 'block'
      })
    },
    // Desktop zones with adjusted heights
    titleZone: {
      height: '60px',
      padding: '16px 24px',
      position: 'relative',
      display: 'flex',
      alignItems: 'center'
    },
    eventZone: {
      height: '40px',
      padding: '0 24px',
      display: 'flex',
      alignItems: 'center'
    },
    dateZone: {
      position: 'absolute',
      bottom: '70px', // Moved up to reduce gap
      left: 0,
      right: 0,
      height: '30px',
      padding: '0 24px',
      display: 'flex',
      alignItems: 'center'
    },
    footerZone: {
      height: '60px',
      padding: '0 24px',
      borderTop: '1px solid #f3f4f6',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0
    },
    // Mobile styles
    mobileContentWrapper: {
      padding: '16px',
      paddingBottom: '90px' // Space for date and footer
    },
    mobileTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: '8px',
      paddingRight: '60px',
      lineHeight: '1.3'
    },
    mobileEvent: {
      fontSize: '13px',
      color: theme.colors.darkGray,
      marginBottom: '8px'
    },
    mobileDate: {
      position: 'absolute',
      bottom: '50px', // Just above footer
      left: '16px',
      right: '16px',
      fontSize: '12px',
      color: theme.colors.darkGray,
      fontStyle: 'italic'
    },
    mobileFooter: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '50px',
      padding: '0 16px',
      borderTop: '1px solid #f3f4f6',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: 'white',
      borderBottomLeftRadius: '8px',
      borderBottomRightRadius: '8px'
    },
    // Common styles
    typeIndicator: {
      position: 'absolute',
      top: isMobileView ? '12px' : '20px',
      right: isMobileView ? '12px' : '20px',
      fontSize: '11px',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: getTypeColor(pubData.type),
      backgroundColor: `${getTypeColor(pubData.type)}10`,
      padding: '4px 12px',
      borderRadius: '12px'
    },
    title: {
      fontSize: '18px',
      fontWeight: '600',
      color: theme.colors.text,
      lineHeight: '1.4',
      paddingRight: '80px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical'
    },
    event: {
      fontSize: '14px',
      color: theme.colors.darkGray,
      lineHeight: '1.5',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis'
    },
    date: {
      fontSize: '13px',
      color: theme.colors.darkGray,
      fontStyle: 'italic'
    },
    author: {
      fontSize: '13px',
      color: theme.colors.darkGray,
      fontWeight: '500',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      maxWidth: '60%'
    },
    viewButton: {
      fontSize: '13px',
      color: theme.colors.primary,
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      textDecoration: 'none',
      whiteSpace: 'nowrap'
    },
    unavailable: {
      fontSize: '12px',
      color: '#9ca3af',
      fontStyle: 'italic'
    }
  };

  return (
    <div 
      style={cardStyles.container}
      onClick={pubData.pdfUrl ? handleCardClick : undefined}
      onMouseEnter={(e) => {
        if (pubData.pdfUrl && !isMobileView) {
          e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }
      }}
      onMouseLeave={(e) => {
        if (pubData.pdfUrl && !isMobileView) {
          e.currentTarget.style.boxShadow = 'none';
          e.currentTarget.style.transform = 'translateY(0)';
        }
      }}
    >
      {/* Type indicator */}
      <span style={cardStyles.typeIndicator}>
        {pubData.type || 'Publication'}
      </span>

      {isMobileView ? (
        // Mobile Layout - With anchored date
        <>
          <div style={cardStyles.mobileContentWrapper}>
            <h3 style={cardStyles.mobileTitle}>
              {pubData.title || 'Untitled Publication'}
            </h3>
            
            {pubData.event && (
              <div style={cardStyles.mobileEvent}>
                {pubData.event}
              </div>
            )}
          </div>
          
          {/* Date - Positioned absolutely above footer */}
          <div style={cardStyles.mobileDate}>
            {pubData.originalDate || '\u00A0'}
          </div>
          
          {/* Footer - Positioned absolutely at bottom */}
          <div style={cardStyles.mobileFooter}>
            <span style={cardStyles.author}>
              {pubData.author || 'Unknown Author'}
            </span>
            
            {pubData.pdfUrl ? (
              <span style={cardStyles.viewButton}>
                View PDF
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M7 17L17 7M17 7H7M17 7V17" />
                </svg>
              </span>
            ) : (
              <span style={cardStyles.unavailable}>
                PDF not available
              </span>
            )}
          </div>
        </>
      ) : (
        // Desktop Layout - Fixed Zones
        <>
          {/* Zone 1: Title (60px) */}
          <div style={cardStyles.titleZone}>
            <h3 style={cardStyles.title}>
              {pubData.title || 'Untitled Publication'}
            </h3>
          </div>
          
          {/* Zone 2: Event (40px) */}
          <div style={cardStyles.eventZone}>
            <div style={cardStyles.event}>
              {pubData.event || '\u00A0'} {/* Non-breaking space for empty */}
            </div>
          </div>
          
          {/* Zone 3: Date (40px) */}
          <div style={cardStyles.dateZone}>
            <div style={cardStyles.date}>
              {pubData.originalDate || '\u00A0'} {/* Non-breaking space for empty */}
            </div>
          </div>
          
          {/* Zone 4: Footer (60px) - Positioned absolutely at bottom */}
          <div style={cardStyles.footerZone}>
            <span style={cardStyles.author}>
              {pubData.author || 'Unknown Author'}
            </span>
            
            {pubData.pdfUrl ? (
              <span style={cardStyles.viewButton}>
                View PDF
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M7 17L17 7M17 7H7M17 7V17" />
                </svg>
              </span>
            ) : (
              <span style={cardStyles.unavailable}>
                PDF not available
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PublicationCard;